import React, { useEffect, useRef, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet";
import { ScrollParallax } from "react-just-parallax";
import { SlArrowDown } from "react-icons/sl";
import { FaLinkedin, FaFacebook, FaWhatsapp } from "react-icons/fa";
import { apiPost } from "../../../utils/apiPost";
import SuggestedPost from "./SuggestedPost";

export default function SingleArticle({ isBlog = false }) {
  const [postNew, setPostNew] = useState({});
  const [urlImag, setUrlImg] = useState("");
  const [imagePath, setImagePath] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  const nextSectionRef = useRef(null);

  const postUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${location.pathname}`
      : `https://www.careisgold.it${location.pathname}`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.post(
          `https://crm.careholding.it/ws/Call/?Cat=Blog&met=GetPost&np=1`,
          { id }
        );

        const article = response.data;

        if (!article || !article.RewriteUrl) {
          navigate("/not-found", { replace: true });
          return;
        }

        const currentPath = location.pathname.endsWith("/")
          ? location.pathname
          : `${location.pathname}/`;

        const correctPath = `/notizie/${article.RewriteUrl.replace(/^\/+|\/+$/g, "")}/`;

        if (currentPath !== correctPath) {
          navigate(correctPath, { replace: true });
          return;
        }

        setPostNew(article);

        if (article.ImgCopertina) {
          const parts = article.ImgCopertina.split("=");
          if (parts[2]) {
            setUrlImg(parts[2]);
          }
        }
      } catch (error) {
        console.error("An error occurred:", error);
        navigate("/not-found", { replace: true });
      }
    };

    fetchData();
  }, [id, location.pathname, navigate]);

  useEffect(() => {
    if (urlImag) {
      try {
        const image = require(`../../../assets/blog/img_${urlImag}.jpg`);
        setImagePath(image);
      } catch (error) {
        console.error("Image not found:", error);
        setImagePath("");
      }
    }
  }, [urlImag]);

  const scrollToNextSection = () => {
    nextSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleClickShare = async () => {
    try {
      await apiPost({
        cat: "Blog",
        met: "IncrementShare",
        np: 1,
        body: {
          id: id,
        },
      });
    } catch (error) {
      console.log(error);
    }
  };

  const openShareWindow = (url) => {
    handleClickShare();
    const isSmallDevice = window.matchMedia("(max-width: 768px)").matches;
    const timeout = isSmallDevice ? 450 : 0;

    setTimeout(() => {
      window.open(url, "_blank", "noopener,noreferrer");
    }, timeout);
  };

  return (
    <>
      <Helmet>
        <title>{postNew.Metatitle || ""}</title>
        <meta name="description" content={postNew.Metadescription || ""} />
        <meta name="keywords" content={postNew.Keyword || ""} />

        <meta property="og:title" content={postNew.Metatitle || ""} />
        <meta property="og:description" content={postNew.Metadescription || ""} />
        <meta
          property="og:url"
          content={
            postNew.RewriteUrl
              ? `https://www.careisgold.it/notizie/${postNew.RewriteUrl.replace(/^\/+|\/+$/g, "")}/`
              : postUrl
          }
        />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={imagePath || postNew.ImgCopertina || ""} />
        <meta name="author" content="careisgold s.p.a" />

        <link
          rel="canonical"
          href={
            postNew.RewriteUrl
              ? `https://www.careisgold.it/notizie/${postNew.RewriteUrl.replace(/^\/+|\/+$/g, "")}/`
              : postUrl
          }
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "http://schema.org",
            "@type": "BlogPosting",
            headline: postNew.Metatitle,
            description: postNew.Metadescription,
            image: imagePath || postNew.ImgCopertina,
            url: postNew.RewriteUrl
              ? `https://www.careisgold.it/notizie/${postNew.RewriteUrl.replace(/^\/+|\/+$/g, "")}/`
              : postUrl,
            datePublished: postNew.DataInserimento,
            dateModified: postNew.DataModifica,
            author: {
              "@type": "Organization",
              name: "Careisgold S.p.A",
            },
            publisher: {
              "@type": "Organization",
              name: "Careisgold S.p.A",
              logo: {
                "@type": "ImageObject",
                url: "https://www.careisgold.it/static/media/logo1.25b1cf4e392fc1ad4340.webp/",
              },
            },
          })}
        </script>
      </Helmet>

      <div className="relative z-20 h-lvh overflow-hidden">
        <ScrollParallax
          enableOnTouchDevice={true}
          isAbsolutelyPositioned={true}
          lerpEase="0.05"
        >
          <img
            src={postNew.ImgHeader}
            alt={postNew.Titolo}
            className="absolute top-0 left-0 w-full h-full object-cover"
          />
        </ScrollParallax>

        <div className="absolute top-0 left-0 bg-black bg-opacity-40 w-full h-full flex justify-center items-center">
          {postNew.ImgLogo && (
            <img
              src={postNew.ImgLogo}
              alt="background"
              className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 lg:left-24 lg:top-1/2 top-0 lg:transform lg:-translate-y-1/2 lg:-translate-x-0 object-contain"
            />
          )}

          <div className="absolute text-start ps-15 lg:ps-96 ml-[5%] text-balance">
            {postNew.strDataCare !== " ." && postNew.strDataCare !== "." && (
              <div className="text-neutral-50 p-2 text-xl">
                <em>{postNew.strDataCare}</em>
              </div>
            )}

            <h1
              className="text-5xl text-white font-semibold uppercase"
              data-aos="fade-up"
              data-aos-duration="1000"
            >
              {postNew.Titolo}
            </h1>

            <p
              className="text-xl mt-4 mb-10 text-white"
              data-aos="fade-up"
              data-aos-duration="1000"
            >
              {postNew.Subtitle}
            </p>
          </div>

          <SlArrowDown
            data-aos="fade-up"
            data-aos-delay="600"
            data-aos-duration="1000"
            onClick={scrollToNextSection}
            className="arrow cursor-pointer block absolute transform -translate-x-1/2 text-slate-50 text-3xl md:text-5xl"
          />
        </div>
      </div>

      <section className="relative bg-blog" ref={nextSectionRef}>
        {postNew.ImgLogo && (
          <img
            src={postNew.ImgLogo}
            alt="background"
            className="block lg:hidden relative left-1/2 transform -translate-x-1/2 top-0 object-contain"
          />
        )}

        <div className="h-full flex justify-center items-center">
          <div className="w-full sm:w-3/4 text-black flex flex-col justify-center items-center pb-4 px-4 lg:pt-36 z-20">
            <div
              className="text-left w-full mx-auto md:w-9/12 text-content"
              id="blogContainer"
            >
              <div
                className="text-lg mb-10"
                data-aos="fade-zoom-in"
                data-aos-duration="1000"
                dangerouslySetInnerHTML={{ __html: postNew.html }}
              ></div>
            </div>

            {!isBlog && (
              <div className="w-full xl:w-1/2">
                <a href={postNew.LinkPdf} target="_blank" rel="noopener noreferrer">
                  <button
                    className="progress-button-dark border-black text-black px-8 py-2 uppercase border text-center transition duration-300 ease-in-out w-full"
                  >
                    {postNew.LinkPdf?.startsWith(
                      "https://crm.careholding.it/FileOpen.axd?"
                    )
                      ? "Scarica PDF"
                      : "Leggi l'articolo completo"}
                  </button>
                </a>
              </div>
            )}

            <div className="w-full">
              <div className="flex flex-col items-center lg:flex-row justify-center space-y-4 lg:space-x-4 lg:space-y-0 my-10">
                <button
                  onClick={() =>
                    openShareWindow(
                      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                        postUrl
                      )}`
                    )
                  }
                  className="text-blue-600 hover:text-slate-50 border-[0.5px] border-blue-600 font-semibold py-2 px-4 progress-button-linkedin transition duration-300 ease-in-out"
                >
                  <FaLinkedin className="inline-block mr-2" /> Condividi su LinkedIn
                </button>

                <button
                  onClick={() =>
                    openShareWindow(
                      `https://www.facebook.com/dialog/share?app_id=1211095037237783&display=popup&href=${encodeURIComponent(
                        postUrl
                      )}&redirect_uri=${encodeURIComponent(postUrl)}`
                    )
                  }
                  className="text-blue-500 hover:text-slate-50 border-[0.5px] border-blue-500 font-semibold py-2 px-4 progress-button-facebook transition duration-300 ease-in-out"
                >
                  <FaFacebook className="inline-block mr-2" /> Condividi su Facebook
                </button>

                <button
                  onClick={() =>
                    openShareWindow(
                      `https://api.whatsapp.com/send?text=${encodeURIComponent(
                        postUrl
                      )}`
                    )
                  }
                  className="text-green-500 hover:text-slate-50 border-[0.5px] border-green-500 font-semibold py-2 px-4 progress-button-whatsapp transition duration-300 ease-in-out"
                >
                  <FaWhatsapp className="inline-block mr-2" /> Condividi su WhatsApp
                </button>
              </div>
            </div>

            <section className="container mx-auto py-8">
              <SuggestedPost
                idCategory={postNew.IdCategory}
                postId={id}
                apiName={
                  isBlog
                    ? "https://crm.careholding.it/ws/Call/?Cat=Blog&met=GetPostsBlog&np=0"
                    : "https://crm.careholding.it/ws/Call/?Cat=Blog&met=GetPostsRS&np=0"
                }
              />
            </section>
          </div>
        </div>
      </section>
    </>
  );
}